import Cookies from "js-cookie";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { useCustomerAnalytics } from "../../../services/hooks/useCustomerAnalytics";
import { useModalHook } from "../../../services/hooks/useModal";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../services/hooks/useReduxHooks";
import { selectCartItemsNumber } from "../../../services/redux/selectors/cartSelectors";
import { useTranslationI18 } from "../../../services/hooks/useTranslationI18";
import { setSelectedFranchise } from "../../../services/redux/slices/configSlice";
import {
  setBackPage,
  setCurrentDeliveryConfig,
} from "../../../services/redux/slices/deliverySlice";
import {
  setBottomSheetSource,
  setBottomSheetState,
  setOrderDataLoading,
} from "../../../services/redux/slices/globalComponentsToggleSlice";
import { setClickDetected } from "../../../services/redux/slices/locationSlice";
import { setQueryParam } from "../../../services/redux/slices/urlParamsSlice";
import { setTableNumber } from "../../../services/redux/slices/userInfoSlice";
import { cn } from "../../../utils/cn";
import {
  COOKIE_ENUM,
  CUSTOMER_ANALYTICS_EVENT,
  OrderTypesEnum,
  PagesEnum,
} from "../../../utils/enums";
import { getPageLink } from "../../../utils/help/navigation/navigation";
import {
  getFranchiseAvailableOrderTypes,
  isOnlyOneOrderTypePerFranchise,
} from "../../../utils/help/orderType/orderType";
import {
  Franchise,
  FranchiseDeliveryConfig,
  NewQueryParamsType,
  Restaurant,
} from "../../../utils/types";
import { LangSwitch } from "../../global/LangSwitch/LangSwitch";
import {
  LocationDropdown,
  LocationDropdownType,
} from "../../global/LocationsDropdown/LocationDropdown";
import {
  OrderTypeToggle,
  OrderTypeToggleType,
} from "../../global/NewHeader/OrderType/OrderType.component";
import { SelectOrderInfoV3 } from "./SelectOrderInfoV3";
import styles from "./SelectOrderInfo.module.scss";
import { findFranchiseBySlug } from "../../../utils/help/franchise/findFranchiseBySlug";
import { selectCurrentDeliveryConfig } from "../../../services/redux/selectors/deliverySelectors";

type SelectOrderInfoProps = {
  restaurantInfo: Restaurant;
  disableOrderType?: boolean;
};

function handleDropDownHeight(length: number): string {
  const heightClasses: Record<number, string> = {
    2: "dropdown_height_2",
    3: "dropdown_height_3",
    4: "dropdown_height_4",
  };

  if (length >= 5) {
    return "dropdown_height_5";
  }

  return heightClasses[length];
}

function getEffectiveOrderType(
  franchise: Franchise | undefined,
  selectedOrderType: OrderTypesEnum | undefined,
  deliveryV2Config?: FranchiseDeliveryConfig | null
): OrderTypesEnum | undefined {
  if (franchise && isOnlyOneOrderTypePerFranchise(franchise.franchiseConfig)) {
    const orderTypes = getFranchiseAvailableOrderTypes(
      franchise.franchiseConfig
    );

    return orderTypes[0];
  }

  if (selectedOrderType) {
    if (selectedOrderType === OrderTypesEnum.DELIVERYV2 && !deliveryV2Config) {
      return undefined;
    }

    return selectedOrderType;
  }

  return undefined;
}

export default function SelectOrderInfo(props: SelectOrderInfoProps) {
  const hasWelcomeDrawerV3 = useAppSelector(
    (state) => state.config.restaurantConfig?.hasWelcomeDrawerV3
  );

  if (hasWelcomeDrawerV3) {
    return <SelectOrderInfoV3 restaurantInfo={props.restaurantInfo} />;
  }

  return <SelectOrderInfoV2 {...props} />;
}

function SelectOrderInfoV2(props: SelectOrderInfoProps) {
  const { disableOrderType } = props;
  const { t } = useTranslationI18();
  const [values, setValues] = useState<{
    orderType?: OrderTypesEnum;
    franchise?: Franchise;
    deliveryV2Config?: FranchiseDeliveryConfig;
  } | null>(null);
  const [orderTypeOpened, setOrderTypeOpened] = useState(false);
  const [franchiseOpened, setFranchiseOpened] = useState(false);
  const addCustomerAnalytics = useCustomerAnalytics();

  const router = useRouter();
  const offlineModeScreen = router.pathname.includes(PagesEnum.OfflineMode);
  const { handleModal } = useModalHook();

  const { franchise, order_type, partnerlogin, table } = router.query;

  const deliveryV2Config = useAppSelector(selectCurrentDeliveryConfig);

  const activeFranchises = useAppSelector(
    (state) => state.config.activeFranchises
  );

  const selectedFranchise = useAppSelector(
    (state) => state.config.selectedFranchise
  );

  const orderTypeParamState = useAppSelector(
    (state) => state.queryParams.orderTypeParam
  );
  const bottomSheetSource = useAppSelector(
    (state) =>
      state.globalComponentsToggleSlice.bottomSheetModal?.source ?? null
  );

  const restaurantConfig = useAppSelector(
    (state) => state.config.restaurantConfig
  );
  const hasOfflineMode = restaurantConfig?.hasOfflineMode;

  // Raw cartLoyaltyV2.cart.cartItemsNumber is 0 when `useLoyaltyService` is on,
  // which skipped the branch-switch confirmation for those restaurants.
  const cartItemsNumber = useAppSelector(selectCartItemsNumber);

  const orderTypes = getFranchiseAvailableOrderTypes(
    values?.franchise?.franchiseConfig || selectedFranchise?.franchiseConfig
  );

  const showSkipButton = !(
    activeFranchises.length === 1 &&
    isOnlyOneOrderTypePerFranchise(activeFranchises[0].franchiseConfig)
  );

  const shouldSkipOrderTypeSelection =
    (hasOfflineMode && offlineModeScreen && values?.franchise) ||
    isOnlyOneOrderTypePerFranchise(values?.franchise?.franchiseConfig);

  const dispatch = useAppDispatch();
  const token = useAppSelector((state) => state.userInfo.token);

  /**
   * Waits for the token instead of racing it.
   *
   * `useCustomerAnalytics` drops any event that is not in
   * VISITOR_ANALYTICS_EVENTS when there is no TOKEN cookie, and this one is not
   * in that list. A bare mount effect therefore only reported the drawer if the
   * visitor token happened to already be set -- which it was, as long as the
   * config arrived from a browser request and this drawer mounted after that
   * round trip. Now the config comes from the page, the drawer mounts sooner,
   * and the event was being dropped on every first visit with nothing retrying
   * it. #4204
   *
   * The ref keeps it to one report per mount: `token` moves from absent to set
   * as the visitor is issued one, and the drawer opening is a single event.
   */
  const reportedDrawerOpened = useRef(false);

  useEffect(() => {
    if (reportedDrawerOpened.current || !token) {
      return;
    }

    reportedDrawerOpened.current = true;
    addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.WELCOME_DRAWER_OPENED);
  }, [token]);

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    if (selectedFranchise && orderTypeParamState) {
      setValues({
        franchise: selectedFranchise,
        orderType: orderTypeParamState,
      });

      return;
    }

    if (franchise) {
      const selectedActiveFranchise = findFranchiseBySlug(
        activeFranchises,
        franchise as string,
        selectedFranchise
      );

      if (selectedActiveFranchise) {
        const selectedFranchiseOrderTypes = getFranchiseAvailableOrderTypes(
          selectedActiveFranchise?.franchiseConfig
        );

        const fromTableQR = Boolean(table);
        const dineInTable =
          fromTableQR &&
          selectedFranchiseOrderTypes.includes(OrderTypesEnum.DINE_IN)
            ? OrderTypesEnum.DINE_IN
            : undefined;

        const selectedFranchiseOrderType =
          dineInTable ??
          (order_type &&
          selectedFranchiseOrderTypes.includes(order_type as OrderTypesEnum)
            ? (order_type as OrderTypesEnum)
            : undefined);

        setValues({
          ...values,
          franchise: selectedActiveFranchise,
          orderType: selectedFranchiseOrderType,
          deliveryV2Config:
            selectedActiveFranchise?.franchiseConfig?.deliveryV2?.[0] ??
            undefined,
        });

        return;
      }
    }

    if (
      activeFranchises.length === 1 ||
      (hasOfflineMode && offlineModeScreen)
    ) {
      dispatch(setSelectedFranchise(activeFranchises[0]));
      setValues({
        ...values,
        franchise: activeFranchises[0],
        orderType: undefined,
      });
    }
  }, [router.isReady, activeFranchises.length]);

  useEffect(() => {
    if (shouldSkipOrderTypeSelection) {
      setValues({
        ...values,
        orderType: orderTypes[0],
      });

      if (orderTypes[0] === OrderTypesEnum.DELIVERYV2) {
        dispatch(
          setCurrentDeliveryConfig(
            values?.franchise?.franchiseConfig?.deliveryV2![0] ?? null
          )
        );
      }
    } else if (
      selectedFranchise &&
      !values?.franchise &&
      !orderTypeParamState
    ) {
      // A franchise is already selected in redux (persisted from a previous
      // visit, auto-detected, etc.) but the local form state hasn't picked it
      // up. Mirror it into `values` so the order-type input is interactive and
      // the proceed button is enabled — otherwise the user is stuck with a
      // branch that looks selected but can only be unblocked by reselecting it.
      // Gated on `!orderTypeParamState` so it never competes with the first
      // effect, which already seeds values when an order-type param exists.
      const effectiveOrderType = getEffectiveOrderType(
        selectedFranchise,
        undefined,
        deliveryV2Config
      );

      setValues({
        franchise: selectedFranchise,
        orderType: effectiveOrderType,
      });

      if (effectiveOrderType === OrderTypesEnum.DELIVERYV2) {
        dispatch(
          setCurrentDeliveryConfig(
            selectedFranchise?.franchiseConfig?.deliveryV2![0] ?? null
          )
        );
      }
    }
  }, [selectedFranchise, values?.franchise?.id]);

  useEffect(() => {
    if (orderTypeOpened) {
      setFranchiseOpened(false);
    }
  }, [orderTypeOpened]);

  useEffect(() => {
    if (franchiseOpened) {
      setOrderTypeOpened(false);
    }
  }, [franchiseOpened]);

  const selectOrderInfo = () => {
    if (!values?.franchise) {
      return;
    }

    const chooseOrderInfoDate: Date = new Date();
    chooseOrderInfoDate.setHours(chooseOrderInfoDate.getHours() + 2);
    const chooseOrderInfoExpires: Date = new Date(chooseOrderInfoDate);

    Cookies.set(
      COOKIE_ENUM.LAST_SELECTED_FRANCHISE,
      // slug can be null on the slug-less main franchise; fall back to id.
      String(values?.franchise.slug ?? values?.franchise.id),
      {
        expires: chooseOrderInfoExpires,
      }
    );

    dispatch(setClickDetected(true));

    dispatch(setOrderDataLoading(true));
    const selectedOrderType = values?.orderType || orderTypes[0];
    const currentOrderType = getEffectiveOrderType(
      selectedFranchise,
      orderTypeParamState,
      deliveryV2Config
    );
    const isCartContext =
      router.pathname.includes(PagesEnum.Cart) ||
      router.pathname.includes(PagesEnum.Tip);

    addCustomerAnalytics(
      CUSTOMER_ANALYTICS_EVENT.CONFIRM_BRANCH_AND_ORDER_TYPE,
      {
        franchiseId: values?.franchise.id,
        franchiseSlug: values?.franchise.slug,
        orderType: selectedOrderType,
      },
      values?.franchise.id,
      values?.franchise.slug
    );

    if (
      isCartContext &&
      selectedFranchise?.id === values?.franchise.id &&
      currentOrderType &&
      selectedOrderType !== currentOrderType
    ) {
      addCustomerAnalytics(
        CUSTOMER_ANALYTICS_EVENT.CHANGE_ORDER_TYPE_FROM_CART,
        {
          orderType: selectedOrderType,
          franchiseId: values?.franchise.id,
          franchiseSlug: values?.franchise.slug,
        }
      );
    }
    if (bottomSheetSource === "burger_menu") {
      addCustomerAnalytics(
        CUSTOMER_ANALYTICS_EVENT.BRANCH_AND_ORDER_TYPE_FROM_BURGER_MENU
      );
    } else if (bottomSheetSource === "item_selection") {
      addCustomerAnalytics(
        CUSTOMER_ANALYTICS_EVENT.BRANCH_AND_ORDER_TYPE_FROM_ITEM_SELECTION
      );
    } else if (bottomSheetSource === "banner_selection") {
      addCustomerAnalytics(
        CUSTOMER_ANALYTICS_EVENT.BRANCH_AND_ORDER_TYPE_SELECTION_FROM_BANNERS
      );
    }

    dispatch(setBottomSheetState(false));
    dispatch(setBottomSheetSource(null));

    let newQueryParams: NewQueryParamsType = {
      ...router.query,
    };

    const isFranchiseChanging =
      franchise && franchise !== values?.franchise.slug;
    const urlTableNumber = router.query.table;
    const shouldClearTable =
      (isFranchiseChanging || !franchise) && urlTableNumber;

    if (values?.franchise.slug) {
      newQueryParams = {
        ...newQueryParams,
        ...{
          franchise: values?.franchise.slug,
          order_type: values?.orderType || (orderTypes[0] as OrderTypesEnum),
        },
      };

      if (shouldClearTable) {
        delete newQueryParams.table;
        dispatch(setTableNumber(1));
      }
    }

    dispatch(setSelectedFranchise(values?.franchise));
    dispatch(setQueryParam(values?.orderType || orderTypes[0]));

    if (
      selectedOrderType === OrderTypesEnum.TAKE_OUT ||
      selectedOrderType === OrderTypesEnum.DINE_IN
    ) {
      dispatch(setCurrentDeliveryConfig(null));
    }

    if (
      (selectedOrderType === OrderTypesEnum.DELIVERYV2 ||
        selectedOrderType === OrderTypesEnum.DELIVERY) &&
      deliveryV2Config &&
      deliveryV2Config.usesMap
    ) {
      dispatch(setBackPage(PagesEnum.Home));
      router.push(
        getPageLink(router, PagesEnum.Location, {
          franchiseSlug: newQueryParams.franchise,
          orderType: newQueryParams.order_type,
        }).href,
        getPageLink(router, PagesEnum.Location, {
          franchiseSlug: newQueryParams.franchise,
          orderType: newQueryParams.order_type,
        }).as
      );
      dispatch(setOrderDataLoading(false));
    } else {
      if (partnerlogin) {
        router.push(
          getPageLink(router, PagesEnum.PartnerAccount, {
            franchiseSlug: newQueryParams.franchise,
            orderType: newQueryParams.order_type,
          }).href,
          getPageLink(router, PagesEnum.PartnerAccount, {
            franchiseSlug: newQueryParams.franchise,
            orderType: newQueryParams.order_type,
          }).as
        );
      } else {
        router.push({ query: newQueryParams }, undefined, { shallow: true });
      }
    }
  };

  const handleClose = () => {
    addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.WELCOME_DRAWER_SKIPPED);

    if (hasOfflineMode && offlineModeScreen) {
      selectOrderInfo();

      return;
    }

    if (deliveryV2Config) {
      dispatch(setCurrentDeliveryConfig(deliveryV2Config));
    }
    dispatch(setBottomSheetState(false));
    dispatch(setBottomSheetSource(null));
  };

  const effectiveOrderType = getEffectiveOrderType(
    values?.franchise,
    values?.orderType,
    deliveryV2Config
  );
  const shouldDisableProceedButton = !values?.franchise || !effectiveOrderType;

  return (
    <div
      className={styles.select_experience}
      data-testid="select-order-info-drawer"
    >
      <div className={styles.lang_div}>
        <LangSwitch preferredCountry={props.restaurantInfo.preferredCountry} />
      </div>
      <h2>{t("common:select-to-proceed")}</h2>

      <div
        className={cn(
          styles.franchise_container,
          franchiseOpened &&
            styles[handleDropDownHeight(activeFranchises.length)]
        )}
      >
        <h3>{t("common:branch")}</h3>
        <LocationDropdown
          setSelectedFranchise={(franchise) => {
            if (
              franchise.id === values?.franchise?.id &&
              hasOfflineMode &&
              offlineModeScreen
            ) {
              return;
            }

            const effectiveOrderType = disableOrderType
              ? values?.orderType
              : getEffectiveOrderType(franchise, undefined);

            setValues({
              ...values,
              franchise,
              orderType: effectiveOrderType,
            });
            addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.BRANCH_SELECTION, {
              franchiseId: franchise.id,
              franchiseSlug: franchise.slug,
            });

            if (effectiveOrderType === OrderTypesEnum.DELIVERYV2) {
              dispatch(
                setCurrentDeliveryConfig(
                  franchise?.franchiseConfig?.deliveryV2![0] ?? null
                )
              );
            }
          }}
          selectedFranchise={values?.franchise}
          setFranchiseOpened={setFranchiseOpened}
          franchiseOpened={franchiseOpened}
          type={LocationDropdownType.SELECT_ORDER_INFO}
        />
      </div>

      {!(hasOfflineMode && offlineModeScreen) && (
        <div
          className={cn(
            styles.order_type_container,
            values?.franchise || selectedFranchise ? styles.show : styles.hide,
            orderTypeOpened && styles[handleDropDownHeight(orderTypes.length)],
            disableOrderType && styles.order_type_disabled
          )}
        >
          <h3>{t("common:order-type")}</h3>
          <OrderTypeToggle
            selectedFranchise={values?.franchise || selectedFranchise}
            setSelectedOrderType={(orderType, deliveryV2ConfigParam) => {
              if (
                (orderType === OrderTypesEnum.TAKE_OUT ||
                  orderType === OrderTypesEnum.DINE_IN) &&
                deliveryV2Config
              ) {
                dispatch(setCurrentDeliveryConfig(null));
                setValues({
                  ...values,
                  orderType,
                  deliveryV2Config: undefined,
                });
                addCustomerAnalytics(
                  CUSTOMER_ANALYTICS_EVENT.ORDER_TYPE_SELECTION,
                  {
                    orderType,
                  }
                );
              } else {
                setValues({
                  ...values,
                  orderType,
                  deliveryV2Config: deliveryV2ConfigParam,
                });
                addCustomerAnalytics(
                  CUSTOMER_ANALYTICS_EVENT.ORDER_TYPE_SELECTION,
                  {
                    orderType,
                  }
                );
              }
            }}
            setOrderTypeOpened={setOrderTypeOpened}
            orderTypeOpened={orderTypeOpened}
            selectedOrderType={values?.orderType}
            type={OrderTypeToggleType.SELECT_ORDER_INFO}
          />
        </div>
      )}

      <div className={styles.button_container}>
        {showSkipButton && (
          <button
            className={cn(styles.button, styles.btn_cancel)}
            onClick={handleClose}
            data-testid="select-order-info-skip-button"
          >
            {t("common:skip")}
          </button>
        )}
        <button
          className={cn(
            styles.button,
            styles.btn_save,
            !showSkipButton && styles.btn_save_full_width,
            shouldDisableProceedButton && styles.disabled
          )}
          onClick={() => {
            if (
              cartItemsNumber > 0 &&
              selectedFranchise &&
              values?.franchise?.id !== selectedFranchise.id
            ) {
              handleModal(true, "SwitchFranchise/SwitchFranchise", {
                componentProps: {
                  franchise: values?.franchise,
                  returnPage: PagesEnum.Home,
                },
              });
            } else {
              selectOrderInfo();
            }
          }}
          disabled={shouldDisableProceedButton}
          data-testid="select-order-info-proceed-button"
        >
          {t("common:proceed")}
        </button>
      </div>
    </div>
  );
}
